import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = ['canvas']

  static values = { data: Object }

  chart = null

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  renderChart() {
    if (!this.hasCanvasTarget) return

    const data = this.dataValue
    const labels = Object.keys(data).sort()
    const values = labels.map((label) => data[label])

    // Fill in missing dates with zeros
    const filledData = this.fillMissingDates(labels, values)

    this.chart = new Chart(this.canvasTarget, {
      type: 'line',
      data: {
        labels: filledData.labels.map(this.formatDateLabel),
        datasets: [{
          label: 'Views',
          data: filledData.values,
          borderColor: '#111827',
          backgroundColor: 'rgba(17, 24, 39, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title: (items) => {
                const idx = items[0].dataIndex
                return this.formatDateLabel(filledData.labels[idx])
              },
              label: (item) => `${item.raw} view${item.raw !== 1 ? 's' : ''}`
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              maxRotation: 0,
              maxTicksLimit: 7
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: '#e5e7eb'
            },
            ticks: {
              stepSize: 1,
              precision: 0
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
    })
  }

  fillMissingDates(labels, values) {
    if (labels.length === 0) {
      return { labels: [], values: []}
    }

    const filledLabels = []
    const filledValues = []
    const dataMap = Object.fromEntries(labels.map((l, i) => [l, values[i]]))

    // Get date range
    const dates = labels.map((l) => new Date(l))
    const minDate = new Date(Math.min(...dates))
    const maxDate = new Date(Math.max(...dates))

    // Fill all dates in range
    const currentDate = new Date(minDate)
    while (currentDate <= maxDate) {
      const dateStr = currentDate.toISOString().split('T')[0]
      filledLabels.push(dateStr)
      filledValues.push(dataMap[dateStr] || 0)
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return { labels: filledLabels, values: filledValues }
  }

  formatDateLabel(dateStr) {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
}
